// Copyright (C) 2022 Intel Corporation
// Copyright (C) 2023 CVAT.ai Corporation
//
// SPDX-License-Identifier: MIT

import { Config } from '@react-awesome-query-builder/antd';
import asyncFetchUsers from 'components/resource-sorting-filtering/request-users';
import {
    JobState, GetJobStateChinese,
    JobStage, GetJobStageChinese,
} from 'cvat-core/src/enums';

export const config: Partial<Config> = {
    fields: {
        state: {
            label: '状态',
            type: 'select',
            operators: ['select_any_in', 'select_equals'],
            valueSources: ['value'],
            fieldSettings: {
                listValues: [
                    { value: 'new', title: `${GetJobStateChinese(JobState.NEW)}` },
                    { value: 'in progress', title: `${GetJobStateChinese(JobState.IN_PROGRESS)}` },
                    { value: 'rejected', title: `${GetJobStateChinese(JobState.REJECTED)}` },
                    { value: 'completed', title: `${GetJobStateChinese(JobState.COMPLETED)}` },
                ],
            },
        },
        stage: {
            label: '阶段',
            type: 'select',
            operators: ['select_any_in', 'select_equals'],
            valueSources: ['value'],
            fieldSettings: {
                listValues: [
                    { value: 'annotation', title: `${GetJobStageChinese(JobStage.ANNOTATION)}` },
                    { value: 'validation', title: `${GetJobStageChinese(JobStage.VALIDATION)}` },
                    { value: 'inspection', title: `${GetJobStageChinese(JobStage.INSPECTION)}` },
                    { value: 'acceptance', title: `${GetJobStageChinese(JobStage.ACCEPTANCE)}` },
                ],
            },
        },
        dimension: {
            label: '维度',
            type: 'select',
            operators: ['select_equals'],
            valueSources: ['value'],
            fieldSettings: {
                listValues: [
                    { value: '2d', title: '2D' },
                    { value: '3d', title: '3D' },
                ],
            },
        },
        assignee: {
            label: '受托人',
            type: 'select',
            valueSources: ['value'],
            operators: ['select_equals'],
            fieldSettings: {
                useAsyncSearch: true,
                forceAsyncSearch: true,
                asyncFetch: asyncFetchUsers,
            },
        },
        updated_date: {
            label: '更新时间',
            type: 'datetime',
            operators: ['between', 'greater', 'greater_or_equal', 'less', 'less_or_equal'],
        },
        id: {
            label: '作业标识',
            type: 'number',
            operators: ['equal', 'between', 'greater', 'greater_or_equal', 'less', 'less_or_equal'],
            fieldSettings: { min: 0 },
            valueSources: ['value'],
        },
        task_id: {
            label: '任务标识',
            type: 'number',
            operators: ['equal', 'between', 'greater', 'greater_or_equal', 'less', 'less_or_equal'],
            fieldSettings: { min: 0 },
            valueSources: ['value'],
        },
        project_id: {
            label: '项目标识',
            type: 'number',
            operators: ['equal', 'between', 'greater', 'greater_or_equal', 'less', 'less_or_equal'],
            fieldSettings: { min: 0 },
            valueSources: ['value'],
        },
        task_name: {
            label: '任务名称',
            type: 'text',
            valueSources: ['value'],
            operators: ['like'],
        },
        project_name: {
            label: '项目名称',
            type: 'text',
            valueSources: ['value'],
            operators: ['like'],
        },
    },
};

export const localStorageRecentCapacity = 10;
export const localStorageRecentKeyword = 'recentlyAppliedJobsFilters';
export const predefinedFilterValues = {
    分配给我: '{"and":[{"==":[{"var":"assignee"},"<username>"]}]}',
    未完成的: '{"!":{"or":[{"==":[{"var":"state"},"completed"]},{"==":[{"var":"stage"},"acceptance"]}]}}',
    已退回的: '{"and":[{"==":[{"var":"state"},"rejected"]}]}',
    标注中的: '{"and":[{"==":[{"var":"stage"},"annotation"]},{"or":[{"==":[{"var":"state"},"new"]},{"==":[{"var":"state"},"in progress"]}]}]}',
    审核中的: '{"and":[{"==":[{"var":"stage"},"validation"]},{"or":[{"==":[{"var":"state"},"new"]},{"==":[{"var":"state"},"in progress"]},' +
        '{"==":[{"var":"state"},"rejected"]}]}]}',
    质检中的: '{"and":[{"==":[{"var":"stage"},"inspection"]},{"or":[{"==":[{"var":"state"},"new"]},{"==":[{"var":"state"},"in progress"]}]}]}',
    质控中的: '{"and":[{"==":[{"var":"stage"},"qualityctl"]},{"or":[{"==":[{"var":"state"},"new"]},{"==":[{"var":"state"},"in progress"]}]}]}',
    验收中的: '{"and":[{"==":[{"var":"stage"},"acceptance"]},{"or":[{"==":[{"var":"state"},"new"]},{"==":[{"var":"state"},"in progress"]}]}]}',
    已完成的: '{"and":[{"==":[{"var":"state"},"completed"]},{"==":[{"var":"stage"},"acceptance"]}]}',
};
